import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";


interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log al console para debugging — visible en DevTools tanto en dev como
    // en prod. OWASP A09:2021 — solo loggeamos al client console, no
    // exfiltramos a un endpoint sin consentimiento.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Captured:", error);
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">Se ha producido un error inesperado</h2>

            {/* Mostrar mensaje del error en cualquier ambiente (sin leak de
                stack trace en producción) — OWASP A09:2021: error.message
                puede ser informativo sin exponer internals como rutas o secrets. */}
            {this.state.error?.message && (
              <div className="p-3 w-full rounded bg-muted mb-4">
                <p className="text-sm font-mono text-foreground break-words">
                  {this.state.error.message}
                </p>
              </div>
            )}

            {/* Stack trace solo en desarrollo */}
            {import.meta.env.DEV && this.state.error?.stack && (
              <div className="p-4 w-full rounded bg-muted overflow-auto mb-4 max-h-64">
                <pre className="text-xs text-muted-foreground whitespace-break-spaces">
                  {this.state.error.stack}
                </pre>
              </div>
            )}

            {/* Component stack — solo dev */}
            {import.meta.env.DEV && this.state.errorInfo?.componentStack && (
              <div className="p-4 w-full rounded bg-muted overflow-auto mb-6 max-h-48">
                <pre className="text-xs text-muted-foreground whitespace-break-spaces">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            {!import.meta.env.DEV && (
              <p className="text-sm text-muted-foreground mb-6 text-center">
                Recargá la página. Si el problema persiste, copiá el mensaje de arriba y contactá a soporte.
              </p>
            )}

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Recargar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
